import React from 'react';
import { Badge } from './Badge';
import type { SafehavenState } from '../../types';

type LookupStatus = 'missing' | 'pending' | 'verified' | 'failed' | 'expired' | string;

/**
 * The service behind an identity check, as a person would name it.
 *
 * Dojah until Interswitch becomes the first choice with Dojah behind it; a
 * reviewer's override is `manual`. Null for a check with no provider yet.
 */
export function providerName(provider: string | null | undefined): string | null {
  if (!provider) return null;
  const names: Record<string, string> = {
    dojah: 'Dojah',
    interswitch: 'Interswitch',
    smile: 'Smile ID',
    manual: 'Reviewer',
    safehaven: 'Safe Haven',
  };
  return names[provider] ?? provider;
}

const LOOKUP: Record<string, { label: string; variant: 'success' | 'danger' | 'warning' | 'neutral' }> = {
  verified: { label: 'passed', variant: 'success' },
  failed: { label: 'failed', variant: 'danger' },
  pending: { label: 'checking', variant: 'warning' },
  expired: { label: 'expired', variant: 'warning' },
  missing: { label: 'not run', variant: 'neutral' },
};

const SAFEHAVEN: Record<SafehavenState, { label: string; variant: 'success' | 'warning' | 'neutral' }> = {
  verified: { label: 'Safe Haven: confirmed', variant: 'success' },
  code_sent: { label: 'Safe Haven: code sent', variant: 'warning' },
  not_started: { label: 'Safe Haven: not started', variant: 'neutral' },
};

/**
 * The BVN step as the two checks it is.
 *
 * The lookup — named for whichever service answered it — runs first and has to pass before Safe Haven is asked anything; after
 * that they fail separately, and a retry repeats only the one that has not
 * passed. A single "BVN verified" hid which half a stuck customer was on — a
 * Dojah pass with Safe Haven never started looked the same as both passing,
 * and a failed Dojah check was not shown anywhere.
 *
 * Safe Haven reads "not started" until Dojah has passed, because it is never
 * sent the number before then; that is the order working, not a fault.
 */
export const BvnProviders: React.FC<{
  /** The lookup's status. Named `dojah` from when it was the only service. */
  dojah: LookupStatus;
  safehaven: SafehavenState;
  failureReason?: string | null;
  /** Which service ran the lookup. Absent from older servers: reads as Dojah,
   *  which ran every lookup before this was recorded. */
  lookupProvider?: string | null;
}> = ({ dojah, safehaven, failureReason, lookupProvider }) => {
  const name = providerName(lookupProvider) ?? 'Dojah';
  const tone = LOOKUP[dojah] ?? { label: dojah, variant: 'neutral' as const };
  const d = { label: `${name}: ${tone.label}`, variant: tone.variant };
  const s = SAFEHAVEN[safehaven];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Badge variant={d.variant}>{d.label}</Badge>
        <Badge variant={s.variant}>{s.label}</Badge>
      </div>
      {dojah === 'failed' && failureReason ? (
        <span style={{ fontSize: 12, color: 'var(--danger-600)' }}>{failureReason}</span>
      ) : null}
    </div>
  );
};
